import React, { useEffect, useState, useRef } from 'react';

import textNodes from 'domains/text_nodes';
import { handleKeyEvents } from 'utils/handlers';

import type { FontFamily } from 'domains/text_nodes/';

type TextToolsFontFamilyProps = {
  family: FontFamily;
  selectFamily: (family: FontFamily) => void;
};

function TextToolbarSidebarFontFamily(props: TextToolsFontFamilyProps) {
  const { family, selectFamily } = props;
  const [openList, setOpenList] = useState(false);
  const listElem = useRef<HTMLUListElement>(null);

  const handleSelect = (item: FontFamily) => {
    selectFamily(item);
    setOpenList(false);
  };

  const handleMouseUp = () => {
    if (listElem.current) {
      listElem.current.focus();

      const button = listElem.current.querySelector('.texttools__dropdown-item--selected button');
      if (button) {
        button.scrollIntoView({ block: 'center' });
      }
    }
  };

  // handle the arrow keys
  const handleNavigate = ({ key }: React.KeyboardEvent) => {
    if (!listElem.current) {
      setOpenList(true);
    } else {
      const listitems = listElem.current.children as HTMLCollectionOf<HTMLLIElement>;
      const selectedItem = Array.from(listitems).findIndex((i) =>
        i.classList.contains('texttools__dropdown-item--selected')
      );

      if (selectedItem > -1) {
        let newIndex: number | undefined;

        if (key === 'ArrowDown') {
          newIndex = selectedItem === listitems.length - 1 ? 0 : selectedItem + 1;
        } else if (key === 'ArrowUp') {
          newIndex = selectedItem === 0 ? listitems.length - 1 : selectedItem - 1;
        } else if (key === 'ArrowRight') {
          const item = listitems[selectedItem].getAttribute('value');
          if (item) {
            handleSelect(item as FontFamily);
          }
        } else if (key === 'ArrowLeft') {
          setOpenList(false);
        }

        if (newIndex && listitems[newIndex]) {
          listitems[selectedItem].classList.remove('texttools__dropdown-item--selected');
          listitems[newIndex].classList.add('texttools__dropdown-item--selected');
          listitems[newIndex].scrollIntoView({ block: 'center' });
          listitems[newIndex].querySelector('button')?.focus();
        }
      }
    }
  };

  useEffect(() => {
    if (openList && listElem.current) {
      const listitems = listElem.current.children as HTMLCollectionOf<HTMLLIElement>;
      const selectedItem = Array.from(listitems).find((i) => i.getAttribute('value') === family);
      if (selectedItem) {
        selectedItem.focus();
        selectedItem.scrollIntoView({ block: 'center' });
      }
    }
  }, [openList, family]);

  return (
    <div className="texttools-item__text-node font-family">
      <span className="choose-font">Font Style</span>
      <div
        role="listbox"
        className="font-family-selected"
        tabIndex={0}
        style={{ fontFamily: family }}
        onKeyDown={handleKeyEvents({
          open: () => setOpenList(true),
          navigate: handleNavigate,
          close: () => setOpenList(false),
        })}
        onMouseDown={() => {
          setOpenList(!openList);
        }}
        onMouseUp={handleMouseUp}
      >
        {family}
      </div>

      {openList && (
        <ul className="texttools__dropdown-list" ref={listElem}>
          {textNodes.utils.fontList.map((item) => (
            <li
              key={item}
              value={item}
              style={{ fontFamily: item }}
              className={`texttools__dropdown-item${
                item === family ? ' texttools__dropdown-item--selected' : ''
              }`}
            >
              <button
                type="button"
                role="link"
                onClick={(e) => {
                  e.nativeEvent.stopImmediatePropagation();
                  handleSelect(item);
                }}
                onKeyDown={handleKeyEvents({
                  open: () => handleSelect(item),
                  navigate: handleNavigate,
                  close: () => setOpenList(false),
                })}
                onMouseUp={(e) => {
                  e.nativeEvent.stopImmediatePropagation();
                  handleMouseUp();
                }}
              >
                {item}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TextToolbarSidebarFontFamily;
